import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import api from '../api/axios'
import { DEFAULTS } from './defaults'
import { interpolate, renderInline } from './markdown'

/**
 * Supplies every visible text in the portal.
 *
 * Defaults come from the code catalogue; overrides are fetched once at start-up
 * in a single small request. That is why an edit appears "after a refresh" and
 * why there is no polling. The read is anonymous, so the sign-in screen gets its
 * wording too. If the request fails the portal keeps rendering with its defaults
 * rather than showing blanks — an unreachable backend should not empty the
 * labels.
 */

const ContentContext = createContext(null)

export function ContentProvider({ children }) {
  const [overrides, setOverrides] = useState({})
  const [loaded, setLoaded] = useState(false)

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/v1/content')
      setOverrides(data && typeof data === 'object' ? data : {})
    } catch {
      // Defaults are a complete catalogue, so this is a degraded state, not a
      // broken one. Deliberately silent: it would otherwise fire on every
      // signed-out page load.
      setOverrides({})
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const value = useMemo(() => {
    const resolve = (key) =>
      Object.prototype.hasOwnProperty.call(overrides, key)
        ? overrides[key]
        : DEFAULTS[key]

    /** Plain string, for attributes, placeholders and option labels. */
    const t = (key, vars) => {
      const raw = resolve(key)
      // An unknown key renders as the key itself: visible in the UI and
      // greppable, rather than a blank space nobody notices.
      if (raw === undefined) return key
      return interpolate(raw, vars)
    }

    return {
      t,
      resolve,
      overrides,
      loaded,
      refresh: load,
      isOverridden: (key) => Object.prototype.hasOwnProperty.call(overrides, key),
      defaultFor: (key) => DEFAULTS[key],
    }
  }, [overrides, loaded, load])

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>
}

export function useContent() {
  const ctx = useContext(ContentContext)
  if (!ctx) {
    throw new Error('useContent must be used inside <ContentProvider>')
  }
  return ctx
}

/** Convenience hook for the common case. */
export function useT() {
  return useContent().t
}

/**
 * Renders a text with its inline markdown as React nodes.
 *
 * Use this for anything shown as content; use t() where a plain string is
 * required (attributes, placeholders, values passed to other components).
 */
export function T({ k, vars }) {
  const { t } = useContent()
  return <>{renderInline(t(k, vars), k)}</>
}
